import React, { useEffect, useState, Suspense, useMemo, useCallback } from 'react';
import useBundleTranslation from 'i18n';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { HierarchyFolder, DragItem, HomePageFilterState } from 'app/home/index.functions';
import styles from './HierarchyPanel.styles';
import { Box, Stack } from '@mui/material';
import HierarchyItem from './HierarchyItem';
import { useDrop } from 'react-dnd';
import { removeElementFromFolder } from 'app/home/hierarchy/HierarchyPanel.functions';
import { useHomeContext } from 'app/home/context';
import IconMi from 'components/common/icon/IconMi';
import { useNavigate } from 'react-router-dom';
import AddFolderPopup from 'app/home/hierarchy/AddFolderPopup';
import HideCategoriesPopup from 'app/home/hierarchy/HideCategoriesPopup';
import { useIsAdminOrHasPrivilege } from 'hooks/useUserPrivilege';
import { Resizable } from 're-resizable';
import { Theme } from '@mui/material/styles/createTheme';
import { alpha } from '@mui/material/styles';
import FormComponent from 'components/common/form/Form';
import { now } from 'lodash';
import { useSelector } from 'react-redux';
import HierarchyCommonItem from 'app/home/hierarchy/HierarchyCommonItem';
import HierarchySection from 'app/home/hierarchy/HierarchySection';
import NavbarMenu from 'app/navbar/NavbarMenu';
import { HierarchyMenuProvider, useHierarchyMenu } from 'app/home/hierarchy/HierarchyMenuContext';
import ControlledDropdownIcon from 'app/home/hierarchy/ControlledDropdownIcon';

function PinnedItem({ h, level }: { h: HierarchyFolder; level: number }) {
    let k = h.k || '';
    let hasChildren = h.children && h.children.length > 0;

    let paddingLeft = String(level * 28) + 'px';

    return (
        <Box sx={{ ...styles.hierarchyPinnedItem, paddingLeft: paddingLeft }} className={'pinnedItem'} data-key={h.k}>
            {h.name}
        </Box>
    );
}
function TrashBucket() {
    const { t } = useBundleTranslation(['app/home']);

    const ctx = useHomeContext();
    const [refetchData, trashBucketRef] = [ctx.refetchData, ctx.trashBucketRef];

    const [collectedProps, drop] = useDrop(() => ({
        accept: 'tile',
        drop: async (item: DragItem, monitor) => {
            if (!item.sectionKey || !item.sectionKey.startsWith('folder_')) {
                return;
            }
            await removeElementFromFolder(parseInt(item.sectionKey.replace('folder_', '')), item.tile, t);
            refetchData();
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    return (
        <Box ref={trashBucketRef} sx={styles.folderTrash} className={collectedProps.isOver ? 'drag-over' : undefined}>
            <div key="folderTrash" ref={drop}>
                <Box sx={styles.folderTrashIcon}>
                    <IconMi icon={'delete'} sx={{ fontSize: '50px' }} />
                </Box>
                <Box>
                    <Box component={'strong'}>{t('folder_drag_here')}</Box> {t('folder_drag_here_to')} <br />
                    {t('folder_remove_from_folder')}
                </Box>
            </div>
        </Box>
    );
}

function HierarchyPanelContent() {
    const { t } = useBundleTranslation(['app/home']);
    const navigate = useNavigate();
    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const [pinnedFolders, setPinnedFolders] = useState<HierarchyFolder[]>([]);
    const { openMenuId } = useHierarchyMenu();
    const ctx = useHomeContext();
    const [favorites, folders, categories, folderMap, homePageState, setHomePageState, settings] = [
        ctx.favorites,
        ctx.folders,
        ctx.categories,
        ctx.folderMap,
        ctx.homePageState,
        ctx.setHomePageState,
        ctx.homePageSettings,
    ];

    let topPadding = -10;
    const [scrollTop, setScrollTop] = useState(0);
    const [addFolderPopupActive, setAddFolderPopupActive] = useState<boolean>(false);
    const [folderPopupParentId, setFolderPopupParentId] = useState(0);
    const [folderPopupParentName, setFolderPopupParentName] = useState('');
    const [hideCategoriesPopupActive, setHideCategoriesPopupActive] = useState<boolean>(false);
    const [isFolderItemMenuActive, setIsFolderItemMenuActive] = useState<boolean>(false);
    const [isFolderMenuActive, setIsFolderMenuActive] = useState<boolean>(false);
    const [isCategoryMenuActive, setIsCategoryMenuActive] = useState<boolean>(false);
    const [showCreateCategoryForm, setShowCreateCategoryForm] = useState(false);
    const brandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties);

    useEffect(() => {
        if (openMenuId !== null && openMenuId !== 'folder-menu' && isFolderMenuActive) {
            setIsFolderMenuActive(false);
        }
        if (openMenuId !== null && openMenuId !== 'category-menu' && isCategoryMenuActive) {
            setIsCategoryMenuActive(false);
        }
    }, [openMenuId, isFolderMenuActive, isCategoryMenuActive]);

    const onFolderPopupHide = useCallback(() => {
        setFolderPopupParentId(0);
        setFolderPopupParentName('');
        setAddFolderPopupActive(false);
    }, []);

    const handleScroll = (event: any) => {
        //ToDO Discover this functionality and refactor to new layout
        //setScrollTop(event.currentTarget.scrollTop);
        //getPosition();
    };

    const addFolder = useCallback((parentId: number, parentFolderName: string) => {
        setAddFolderPopupActive(true);
        setFolderPopupParentId(parentId);
        setFolderPopupParentName(parentFolderName);
    }, []);

    const hideCategories = useCallback(() => {
        setHideCategoriesPopupActive(true);
    }, []);

    const getPosition = () => {
        //ToDO Discover this functionality and refactor to new layout
        let items = document.getElementsByClassName('hierarchyItem');
        let dataKey = '';
        let parentTop = 0;

        if (document.getElementsByClassName('hierarchyPanelScroll').length)
            parentTop =
                document.getElementsByClassName('hierarchyPanelScroll')[0].getBoundingClientRect().top - topPadding;

        let pinnedItemsHeight = 0;
        if (document.getElementsByClassName('pinnedItems').length)
            pinnedItemsHeight = document.getElementsByClassName('pinnedItems')[0].getBoundingClientRect().height;

        if (items !== null)
            for (let i = 0; i < items.length; i++) {
                const rect = items[i].getBoundingClientRect();
                const t = rect.top - parentTop - pinnedItemsHeight;

                if (scrollTop < t && t < scrollTop + rect.height) {
                    dataKey = String(items[i].getAttribute('data-key'));
                    break;
                }
            }

        let folders: HierarchyFolder[] = [];
        if (dataKey !== '' && undefined !== folderMap[dataKey]) {
            let folder = folderMap[dataKey];
            while (folder.parent_k) {
                folder = folderMap[folder.parent_k];
                folders.push(folder);
            }
        }

        folders.reverse();
        setPinnedFolders(folders);
    };

    const setFilterState = (filterState: Partial<HomePageFilterState>) => {
        setHomePageState({
            ...homePageState,
            filterState: { ...homePageState.filterState, ...filterState },
        });
    };

    const mostPopular = favorites.filter((favorite) => favorite.favorite_content == 'wisdom of crowds');
    const mostPopularElement = mostPopular.length ? mostPopular[0] : undefined;
    const favoritesData = favorites.filter((favorite) => favorite.favorite_content != 'wisdom of crowds');

    const canAddFolder = useIsAdminOrHasPrivilege('PRIV_ADD_FOLDERS');
    const canModifyMySettings = useIsAdminOrHasPrivilege('PRIV_PREFERENCES');

    const sidebarWidth =
        undefined !== homePageState?.hierarchyPanelWidth &&
        'number' === typeof homePageState?.hierarchyPanelWidth &&
        homePageState?.hierarchyPanelWidth > 0
            ? homePageState?.hierarchyPanelWidth
            : 280;

    const hierarchyViewType = brandThemeProperties?.menu?.catalog_left_navigation ?? 'full';
    const isCollapsedCompact = hierarchyViewType == 'compact' && false !== homePageState?.hierarchyPanelOpen;

    useEffect(() => {
        if (!isFolderMenuActive && !isCategoryMenuActive && !isFolderItemMenuActive) {
            document.body.classList.remove('hierarchy-section-active-menu');
        } else {
            document.body.classList.add('hierarchy-section-active-menu');
        }
    }, [isFolderMenuActive, isCategoryMenuActive, isFolderItemMenuActive]);

    const memorizedFavoritesSection = useMemo(
        () => (
            <>
                {favoritesData.length > 0 && (
                    <HierarchySection
                        dataTest="homepage-tree-favorites"
                        icon={'favorite'}
                        label={t('favorites')}
                        type={'favorite'}
                    >
                        {favoritesData.map((h: HierarchyFolder) => (
                            <HierarchyItem key={h.k} h={h} level={0} type="favorites" />
                        ))}
                    </HierarchySection>
                )}
            </>
        ),
        [favoritesData],
    );

    const memorizedFoldersSection = useMemo(
        () => (
            <>
                {(folders.length > 0 || canAddFolder) && (
                    <HierarchySection
                        dataTest="homepage-tree-folders"
                        icon={'folders'}
                        label={t('folders')}
                        type={'folder'}
                        isMenuActive={isFolderMenuActive}
                        actionMenu={
                            canAddFolder && (
                                <Box sx={styles.hierarchyActionBtn}>
                                    <ControlledDropdownIcon
                                        icon="more"
                                        popperSx={styles.hierarchyActionMenuHolder}
                                        menuId="folder-menu"
                                        onChangeState={(isOpen) => {
                                            setIsFolderMenuActive(isOpen);
                                        }}
                                        placement={'bottom-end'}
                                    >
                                        <Box sx={styles.hierarchyActionMenuList}>
                                            <Box sx={styles.hierarchyActionMenuItem} onClick={() => addFolder(0, '')}>
                                                <Box sx={styles.hierarchyActionMenuItemIcon}>
                                                    <IconMi icon={'new'} fontSize={'16'} />
                                                </Box>
                                                <Box sx={styles.hierarchyActionMenuItemLabel}>{t('add_folder')}</Box>
                                            </Box>
                                            {userAuth.userInfo?.isAdmin && (
                                                <Box
                                                    sx={styles.hierarchyActionMenuItem}
                                                    onClick={() => navigate('/notification/folder')}
                                                >
                                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                                        <IconMi icon={'sort-list'} fontSize={'16'} />
                                                    </Box>
                                                    <Box sx={styles.hierarchyActionMenuItemLabel}>
                                                        {t('sort_folders')}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </ControlledDropdownIcon>
                                </Box>
                            )
                        }
                    >
                        {folders.map((h: HierarchyFolder) => (
                            <HierarchyItem
                                key={h.k}
                                h={h}
                                level={0}
                                type="folders"
                                addFolder={addFolder}
                                setIsFolderItemMenuActive={setIsFolderItemMenuActive}
                            />
                        ))}
                    </HierarchySection>
                )}
            </>
        ),
        [folders, canAddFolder, isFolderMenuActive, userAuth.userInfo?.isAdmin],
    );

    const canSortCategories = useIsAdminOrHasPrivilege('PRIV_SORT_CATEGORIES');
    const canCreateCategories = useIsAdminOrHasPrivilege('PRIV_CREATE_CATEGORIES');
    const memorizedCategoriesSection = useMemo(
        () => (
            <>
                {'N' !== settings?.parameters?.show_categories_on_homepage_ind && categories.length > 0 && (
                    <HierarchySection
                        dataTest="homepage-tree-categories"
                        icon={'categories'}
                        label={t('categories')}
                        type={'category'}
                        isMenuActive={isCategoryMenuActive}
                        actionMenu={
                            canModifyMySettings && (
                                <Box sx={styles.hierarchyActionBtn}>
                                    <ControlledDropdownIcon
                                        icon="more"
                                        popperSx={styles.hierarchyActionMenuHolder}
                                        menuId="category-menu"
                                        onChangeState={(isOpen) => {
                                            setIsCategoryMenuActive(isOpen);
                                        }}
                                        placement={'bottom-end'}
                                    >
                                        <Box sx={styles.hierarchyActionMenuList}>
                                            {canCreateCategories && (
                                                <Box
                                                    sx={styles.hierarchyActionMenuItem}
                                                    onClick={() => setShowCreateCategoryForm(true)}
                                                >
                                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                                        <IconMi icon={'new'} fontSize={'16'} />
                                                    </Box>
                                                    <Box sx={styles.hierarchyActionMenuItemLabel}>
                                                        {t('add_category')}
                                                    </Box>
                                                </Box>
                                            )}
                                            {canCreateCategories && canSortCategories && (
                                                <Box
                                                    sx={styles.hierarchyActionMenuItem}
                                                    onClick={() => navigate('/editor/category')}
                                                >
                                                    <Box sx={styles.hierarchyActionMenuItemIcon}>
                                                        <IconMi icon={'sort-list'} fontSize={'16'} />
                                                    </Box>
                                                    <Box sx={styles.hierarchyActionMenuItemLabel}>
                                                        {t('sort_categories')}
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box sx={styles.hierarchyActionMenuItem} onClick={() => hideCategories()}>
                                                <Box sx={styles.hierarchyActionMenuItemIcon}>
                                                    <IconMi icon={'hide'} fontSize={'16'} />
                                                </Box>
                                                <Box sx={styles.hierarchyActionMenuItemLabel}>
                                                    {t('hide_categories')}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ControlledDropdownIcon>
                                </Box>
                            )
                        }
                    >
                        {categories.map((h: HierarchyFolder) => (
                            <HierarchyItem key={h.k} h={h} level={0} type="categories" />
                        ))}
                    </HierarchySection>
                )}
            </>
        ),
        [
            categories,
            settings?.parameters?.show_categories_on_homepage_ind,
            isCategoryMenuActive,
            canModifyMySettings,
            canCreateCategories,
            canSortCategories,
            hideCategories,
        ],
    );

    return (
        <Box
            sx={styles.hierarchyPanelBox}
            className={`dashboard__hierarchy-container ${isCollapsedCompact ? 'dashboard__hierarchy-container--collapsed-compact' : ''}`}
        >
            {(false !== homePageState?.hierarchyPanelOpen || hierarchyViewType == 'compact') && (
                <Stack
                    sx={styles.hierarchyPanelWrapper}
                    className={`hierarchy  hierarchy--view-${hierarchyViewType} hierarchy-${false !== homePageState?.hierarchyPanelOpen ? 'collapsed' : 'expanded'}`}
                >
                    <Resizable
                        defaultSize={{
                            width: sidebarWidth,
                            height: '100%',
                        }}
                        className={'hierarchy-resize-container'}
                        minWidth={192}
                        maxWidth={560}
                        onResizeStart={(event, direction, elementRef) => {
                            elementRef.classList.add('resizing-active');
                        }}
                        onResizeStop={(event, direction, elementRef, delta) => {
                            const elementSize = elementRef.getBoundingClientRect();
                            elementRef.classList.remove('resizing-active');

                            setHomePageState({
                                ...homePageState,
                                hierarchyPanelWidth: elementSize.width,
                            });
                        }}
                        handleComponent={{
                            right: (
                                <Stack
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        height: '100%',
                                        width: '100%',
                                        flexShrink: 0,
                                        borderRight: '1px solid',
                                        borderColor: 'transparent',
                                        transition: 'all 0s ease-in-out .1s',
                                        '&:hover, .resizing-active &': {
                                            borderColor: (theme: Theme) => theme.palette.primary.main,
                                            backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                                        },
                                    }}
                                />
                            ),
                        }}
                        handleStyles={{
                            right: {
                                right: 0,
                                height: '100%',
                                width: '7px',
                                zIndex: 1,
                            },
                        }}
                        enable={{
                            top: false,
                            right: !isCollapsedCompact,
                            bottom: false,
                            left: false,
                            topRight: false,
                            bottomRight: false,
                            bottomLeft: false,
                            topLeft: false,
                        }}
                    >
                        {/*
                        {pinnedFolders.length > 0 && (
                            <Box className={'hierarchy__pinned-items pinnedItems'} sx={styles.hierarchyPanelPinned}>
                                {pinnedFolders.map((h: HierarchyFolder, key) => (
                                    <PinnedItem key={'pinned_' + h.k} h={h} level={key}></PinnedItem>
                                ))}
                            </Box>
                        )}
                        */}
                        <Box sx={styles.hierarchyPanelMain} onScroll={handleScroll}>
                            <div className={'hierarchyPanelScroll'}>
                                <Box sx={styles.hierarchySection} className={'hierarchy-section common-items'}>
                                    <HierarchyCommonItem
                                        label={t('show_all')}
                                        onClick={() =>
                                            setFilterState({
                                                folderSelected: '',
                                                segmentFilter: '',
                                                tagFilter: [],
                                                lastFilterApplied: '',
                                            })
                                        }
                                        isActive={
                                            !homePageState.filterState.folderSelected &&
                                            !homePageState.filterState.segmentFilter &&
                                            !homePageState.filterState.tagFilter.length
                                        }
                                        icon={'show-all'}
                                        dataTest="homepage-tree-show-all"
                                    />

                                    {(userAuth.userInfo?.isAdmin || userAuth.userInfo?.isPowerUser) && (
                                        <HierarchyCommonItem
                                            label={t('my_content')}
                                            onClick={() =>
                                                setFilterState({
                                                    segmentFilter: 'owned',
                                                    folderSelected: '',
                                                    lastFilterApplied: 'folderSelected',
                                                })
                                            }
                                            isActive={homePageState.filterState.segmentFilter === 'owned'}
                                            icon={'user'}
                                            dataTest="homepage-tree-my-content"
                                        />
                                    )}

                                    {mostPopularElement && (
                                        <HierarchyCommonItem
                                            label={t('most_popular')}
                                            onClick={() =>
                                                setFilterState({
                                                    folderSelected: mostPopularElement?.k ?? '',
                                                    lastFilterApplied: 'folderSelected',
                                                })
                                            }
                                            isActive={homePageState.filterState.folderSelected == mostPopularElement.k}
                                            icon={'popular'}
                                            dataTest="homepage-tree-most-popular"
                                        />
                                    )}
                                </Box>
                                {memorizedFavoritesSection}
                                {memorizedFoldersSection}
                                {memorizedCategoriesSection}
                            </div>
                        </Box>
                        {brandThemeProperties?.menu?.main_navigation == 'left' && (
                            <Box sx={styles.navbarMenuHolder}>
                                <NavbarMenu view={'vertical'} dataType={'leftbar'} />
                            </Box>
                        )}
                        <TrashBucket />
                    </Resizable>
                </Stack>
            )}

            {addFolderPopupActive && (
                <Suspense>
                    <AddFolderPopup
                        onHide={onFolderPopupHide}
                        parentFolderId={folderPopupParentId}
                        parentFolderName={folderPopupParentName}
                    />
                </Suspense>
            )}
            {hideCategoriesPopupActive && (
                <Suspense>
                    <HideCategoriesPopup setHideCategoriesPopupActive={setHideCategoriesPopupActive} />
                </Suspense>
            )}
            {showCreateCategoryForm && (
                <FormComponent
                    isPopup
                    popupType={'new'}
                    pk={`data/editor/category/categoryId/form`}
                    uid={`data/editor/category/categoryId/form-${now()}`}
                    settingsUrl={`data/editor/category/categoryId/form`}
                    onHide={() => setShowCreateCategoryForm(false)}
                    afterSave={(response: any) => {
                        if (response.data && response.data.redirectUrl) {
                            setTimeout(() => navigate(response.data.redirectUrl), 100);
                        }
                    }}
                    translationNS={'app/editor/category'}
                />
            )}
        </Box>
    );
}

export default function HierarchyPanel() {
    return (
        <HierarchyMenuProvider>
            <HierarchyPanelContent />
        </HierarchyMenuProvider>
    );
}
